/**
 * Bounded-concurrency map.
 *
 * `Promise.all(ids.map(fetchOne))` starts every request in the same tick. On a
 * multizone with ~130 children that is 130 simultaneous requests.
 *
 * The reason to bound that is SERVER-side, not client-side. Our origins serve
 * HTTP/2 (verified against stage), so the browser's old ~6-connections-per-host
 * limit does not apply — requests multiplex as streams over one connection and
 * the browser will happily run ~100 at once. There is no connection pool to
 * starve.
 *
 * What 130 simultaneous requests do reach is Lambda, which scales by running
 * **one container per concurrent invocation** — so a wide fan-out can force
 * ~130 cold starts at once. Holding the width down means a handful of
 * containers that go warm after the first wave.
 *
 * That trade is real in both directions, so pick `limit` deliberately: a
 * narrower width means fewer cold starts but more sequential waves, and the
 * caller's result lands later. It does NOT reduce the number of round-trips —
 * only a batch endpoint does that (see the `alerts-batch-by-zone-ids`
 * contract).
 *
 * Results keep INPUT order regardless of completion order, so callers can zip
 * them back against their input.
 */

/**
 * Default width. Chosen to keep concurrent Lambda cold starts modest without
 * serialising a large fan-out into dozens of waves — 130 items land in ~11
 * waves rather than ~22. It is NOT derived from any browser connection limit;
 * see the note above.
 */
const DEFAULT_LIMIT = 12;

/**
 * Map `items` through `task`, running at most `limit` concurrently.
 *
 * Rejections propagate — wrap `task` yourself (or use `mapSettledWithLimit`)
 * when partial failure is acceptable.
 */
export async function mapWithLimit<T, R>(
  items: readonly T[],
  task: (item: T, index: number) => Promise<R>,
  limit: number = DEFAULT_LIMIT,
): Promise<R[]> {
  // `Math.floor(NaN)` is NaN, and `Array.from({length: NaN})` yields no
  // workers at all — the call would resolve with an uninitialised array.
  const width = Number.isFinite(limit) ? Math.max(1, Math.floor(limit)) : DEFAULT_LIMIT;
  const results = new Array<R>(items.length);
  let next = 0;

  async function worker(): Promise<void> {
    // Each worker claims the next index until the list is exhausted. Claiming
    // by shared cursor rather than pre-slicing keeps every worker busy when
    // tasks finish at wildly different speeds — which they do here, where one
    // zone answers in 200 ms and another in 9 s.
    while (next < items.length) {
      const index = next;
      next += 1;
      // No value-based existence check: `T` may legitimately include
      // `undefined`, and skipping those would return a sparse array — losing
      // the settled result this promises for every input.
      results[index] = await task(items[index], index);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(width, items.length) }, () => worker()),
  );
  return results;
}

/**
 * `mapWithLimit` that never rejects — the bounded equivalent of
 * `Promise.allSettled`. Use where one missing item must not lose the rest, as
 * with a user-drawn zone that has no precomputed alerts and 404s.
 */
export async function mapSettledWithLimit<T, R>(
  items: readonly T[],
  task: (item: T, index: number) => Promise<R>,
  limit: number = DEFAULT_LIMIT,
): Promise<PromiseSettledResult<R>[]> {
  return mapWithLimit<T, PromiseSettledResult<R>>(
    items,
    async (item, index) => {
      try {
        return { status: 'fulfilled', value: await task(item, index) };
      } catch (reason) {
        return { reason, status: 'rejected' };
      }
    },
    limit,
  );
}
